import { useRef, useState, DragEvent, ChangeEvent } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { read, utils } from 'xlsx';

import { useTextStyles } from 'src/hooks/useTextStyles';
import Button from 'src/components/NewButton';
import TextInput from 'src/components/TextInput';
import { HeaderCell } from 'src/containers/dashboard/garm/components';
import { SelectTableHeader } from './SelectTableHeader';
import useNotification from 'src/hooks/useNotification';

import { ReactComponent as UploadIcon } from 'src/assets/icons/uploadCloud.svg';

/** Mapped header key when user splits rows into sub-campaigns by distinct values */
export const SEPARATE_SUB_CAMPAIGNS_COLUMN = 'Separate Sub-Campaigns';

const MAX_SUB_CAMPAIGN_UNIQUE_VALUES = 12;

/** Distinct trimmed non-empty cell values under the split column (max 12). */
export function getDistinctSubCampaignRowValues(
  data: Record<string, string>[],
  columnKey = SEPARATE_SUB_CAMPAIGNS_COLUMN
): string[] {
  const seen = new Set<string>();
  data.forEach((row) => {
    const raw = row[columnKey];
    if (raw === undefined || raw === null) return;
    const v = String(raw).trim();
    if (v && !seen.has(v)) {
      seen.add(v);
    }
  });
  return Array.from(seen).slice(0, MAX_SUB_CAMPAIGN_UNIQUE_VALUES);
}

const useStyles = makeStyles(() =>
  createStyles({
    importContent: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    },
    dragDropImport: {
      flex: 1,
      position: 'relative',

      '& > input': {
        display: 'none',
      },

      '& > label': {
        display: 'flex',
        border: '1px dashed var(--gray-200, #EAECF0)',
        background: '#FFF',
        padding: '16px 24px',
        height: '100%',
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',

        '&.drag-active': {
          background: '#F7F7F7',
        },

        '& > div': {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
        },
      },

      '& #drag-file-element': {
        position: 'absolute',
        width: '100%',
        height: '100%',
        borderRadius: 8,
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
    },
    loadingOverlay: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      background: 'rgba(255, 255, 255, 0.75)',
      borderRadius: 8,
      zIndex: 2,
    },
    uploadIcon: {
      marginBottom: 8,
      width: 42,
      height: 42,
      borderRadius: 8,
      border: '1px solid var(--gray-200, #EAECF0)',
      padding: 10,
      background: '#FFF',
      boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
    },
    importedData: {
      flex: 1,
      position: 'relative',
      border: '1px solid var(--gray-200, #EAECF0)',
      background: '#FFF',
      padding: '16px 24px',
      height: '100%',
      borderRadius: 8,
    },
    table: {
      flex: 1,
      boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
      border: '1px solid #EAECF0',
      borderRadius: 8,
      background: '#fff',
      minHeight: '100%',
      borderCollapse: 'collapse',
      overflow: 'auto',
      maxHeight: 308,

      '& .MuiTableBody-root': {
        '& .MuiTableRow-root': {
          borderBottom: '1px solid #EAECF0',

          '&:last-child': {
            borderBottom: 'none',
          },

          '& .MuiTableCell-root': {
            borderBottom: 'none',
            borderRight: '1px solid #EAECF0',

            '&:last-child': {
              borderRight: 'none',
            },
          },
        },
      },
    },
  })
);

const FileTypes = ['csv', 'sheet', 'xls', 'xlsx'];

/** Avoid UTC shift when xlsx formats Excel serial dates (e.g. 6/1/26 → 5/31/26). */
const toImportCellString = (value: unknown): string => {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return '';
    }
    const month = value.getMonth() + 1;
    const day = value.getDate();
    const year = value.getFullYear() % 100;
    return `${month}/${day}/${String(year).padStart(2, '0')}`;
  }
  return String(value);
};

interface ImportTableProps {
  data: Record<string, string>[];
  setData: (data: Record<string, string>[]) => void;
  setFile?: (file: File | null) => void;
  step?: number;
  hasImportErr?: boolean;
  /** Called after user confirms folder name; `sourceColumnName` is the uploaded file’s column header (before mapping to Separate Sub-Campaigns). */
  onSubCampaignFolderConfirm?: (
    folder: string,
    sourceColumnName: string
  ) => void;
  /** Called when mapped column changes away from Separate Sub-Campaigns */
  onSubCampaignClear?: () => void;
  /** Last confirmed folder label (shown when reopening the folder dialog). */
  configuredSubCampaignFolder?: string;
  /** Original file column name for the split column (when header is already Separate Sub-Campaigns in the table). */
  configuredSubCampaignSourceColumn?: string;
}

const FieldNames = [
  'Show Title',
  'Episode Identifier',
  'Impression Count',
  'View Date',
];

const IMPORT_MAPPED_FIELD_KEYS = [...FieldNames, SEPARATE_SUB_CAMPAIGNS_COLUMN];

export const ImportTable = ({
  data,
  setData,
  setFile,
  step = 0,
  hasImportErr = false,
  onSubCampaignFolderConfirm,
  onSubCampaignClear,
  configuredSubCampaignFolder,
  configuredSubCampaignSourceColumn,
}: ImportTableProps) => {
  const classes = useStyles();
  const textClasses = useTextStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const { handleError } = useNotification();

  const [dragActive, setDragActive] = useState<boolean>(false);
  const [loadingFile, setLoadingFile] = useState(false);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [folderNameInput, setFolderNameInput] = useState('New Folder');
  const [pendingColumnKey, setPendingColumnKey] = useState<string | null>(null);

  const applyColumnMapping = (
    oldKey: string,
    newHeader: string,
    opts?: { skipSubCampaignNotify?: boolean }
  ) => {
    if (newHeader === oldKey) {
      return;
    }
    const wasSeparate = oldKey === SEPARATE_SUB_CAMPAIGNS_COLUMN;
    if (
      !opts?.skipSubCampaignNotify &&
      wasSeparate &&
      newHeader !== SEPARATE_SUB_CAMPAIGNS_COLUMN
    ) {
      onSubCampaignClear?.();
    }

    const temp = [...data].map((item) => {
      const newItem: Record<string, string> = {};
      Object.keys(item).forEach((key) => {
        if (key === oldKey) {
          if (newHeader !== 'Ignore Column') {
            newItem[newHeader] = item[oldKey];
          }
        } else {
          newItem[key] = item[key];
        }
      });
      return newItem;
    });
    setData(temp);
  };

  const handleFile = (file: File) => {
    if (
      file.size > 100 * 1024 * 1024 ||
      FileTypes.every((type) => !file.type.includes(type))
    ) {
      handleError({}, 'Please upload CSV or XLS file less than 100 MB.');
    } else {
      setLoadingFile(true);
      if (setFile) {
        setFile(file);
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const wb = read(event?.target?.result, { cellDates: true });
          const sheets = wb.SheetNames;
          if (sheets.length) {
            const rawRows = utils.sheet_to_json(wb.Sheets[sheets[0]], {
              raw: true,
              defval: '',
            }) as Record<string, unknown>[];
            const rows: Record<string, string>[] = rawRows.map((row) =>
              Object.fromEntries(
                Object.entries(row).map(([key, value]) => [
                  key,
                  toImportCellString(value),
                ])
              )
            );
            if (rows.length) {
              setData(rows);
            } else {
              handleError({}, 'Please add data to your file.');
            }
          } else {
            handleError({}, 'No sheets found in file.');
          }
        } catch {
          handleError({}, 'Could not read file.');
        } finally {
          setLoadingFile(false);
        }
      };
      reader.onerror = () => {
        setLoadingFile(false);
        handleError({}, 'Failed to read file.');
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleDrag = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleUploadClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const ignoreAllUnselectedColumns = () => {
    if (!data.length) return;
    const keys = Object.keys(data[0]);
    const unselected = keys.filter(
      (key) => !IMPORT_MAPPED_FIELD_KEYS.includes(key)
    );
    if (unselected.length === 0) return;

    const keep = new Set(
      keys.filter((key) => IMPORT_MAPPED_FIELD_KEYS.includes(key))
    );
    setData(
      data.map((row) => {
        const next: Record<string, string> = {};
        keep.forEach((key) => {
          if (key in row) next[key] = row[key];
        });
        return next;
      })
    );
  };

  const hasUnselectedColumns =
    data.length > 0 &&
    Object.keys(data[0]).some((key) => !IMPORT_MAPPED_FIELD_KEYS.includes(key));

  return (
    <Box className={classes.importContent}>
      {step === 0 && (
        <Typography className={textClasses.smBold} style={{ color: '#344054' }}>
          Upload Attribution Sheet
        </Typography>
      )}
      {data.length > 0 && (
        <Box
          display='flex'
          justifyContent='flex-end'
          style={{ marginBottom: 4 }}
        >
          <Button
            variant='outlined'
            fontSize='sm'
            height={36}
            onClick={ignoreAllUnselectedColumns}
            disabled={!hasUnselectedColumns}
          >
            Ignore all unselected columns
          </Button>
        </Box>
      )}
      <Box
        className={classes.dragDropImport}
        onDragEnter={handleDrag}
        style={
          step === 0 && hasImportErr
            ? {
                border: '1px solid #B42318',
                borderRadius: 8,
              }
            : undefined
        }
      >
        <input
          ref={inputRef}
          type='file'
          id='input-file-upload'
          onChange={handleChange}
        />
        {data && data.length ? (
          <TableContainer component={Paper} className={classes.table}>
            <Table>
              <TableHead
                style={{
                  height: 48,
                }}
              >
                <TableRow>
                  {Object.keys(data[0]).map((column: string) => (
                    <HeaderCell
                      key={column}
                      style={{
                        padding: '4px 10px',
                        borderRadius: 0,
                        borderBottom: 'none',
                        background:
                          step === 1 &&
                          !IMPORT_MAPPED_FIELD_KEYS.includes(column)
                            ? '#FEDF89'
                            : '#F8F8F8',
                      }}
                    >
                      <SelectTableHeader
                        options={[
                          ...FieldNames,
                          SEPARATE_SUB_CAMPAIGNS_COLUMN,
                          'Ignore Column',
                        ]}
                        header={column}
                        accentBlueOption={SEPARATE_SUB_CAMPAIGNS_COLUMN}
                        setHeader={(newHeader) =>
                          applyColumnMapping(column, newHeader)
                        }
                        interceptOption={(option) => {
                          if (option !== SEPARATE_SUB_CAMPAIGNS_COLUMN) {
                            return false;
                          }
                          setPendingColumnKey(column);
                          setFolderNameInput(
                            configuredSubCampaignFolder?.trim() || 'New Folder'
                          );
                          setFolderDialogOpen(true);
                          return true;
                        }}
                      />
                    </HeaderCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.slice(0, 50).map((item, index) => (
                  <TableRow
                    key={index}
                    style={{
                      height: '48px',
                    }}
                  >
                    {Object.keys(item).map((key) => (
                      <TableCell
                        key={key}
                        style={{
                          background:
                            step === 1 &&
                            !IMPORT_MAPPED_FIELD_KEYS.includes(key)
                              ? '#FEDF89'
                              : '#FFF',
                        }}
                      >
                        <Typography
                          className={textClasses.xsRegular}
                          style={{
                            color: '#475467',
                          }}
                        >
                          {item[key]}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <label
            id='label-file-upload'
            htmlFor='input-file-upload'
            className={dragActive ? 'drag-active' : ''}
          >
            <div>
              <div className={classes.uploadIcon}>
                <UploadIcon width={20} height={20} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Button
                  variant='text'
                  fontSize='sm'
                  height={20}
                  onClick={handleUploadClick}
                >
                  <Typography
                    className={textClasses.smBold}
                    style={{ color: '#5EA1F2' }}
                  >
                    Click to upload
                  </Typography>
                </Button>
                <Typography
                  className={textClasses.smRegular}
                  style={{ color: '#475467' }}
                >
                  or drag and drop
                </Typography>
              </div>
              <Typography
                className={textClasses.xsRegular}
                style={{ color: '#475467' }}
              >
                CSV or XLS
              </Typography>
            </div>
          </label>
        )}
        {loadingFile && (
          <Box className={classes.loadingOverlay}>
            <CircularProgress size={32} style={{ color: '#5EA1F2' }} />
            <Typography
              className={textClasses.smRegular}
              style={{ color: '#475467' }}
            >
              Processing file…
            </Typography>
          </Box>
        )}
        {dragActive && (
          <div
            id='drag-file-element'
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          />
        )}
      </Box>
      <Dialog
        open={folderDialogOpen}
        onClose={() => {
          setFolderDialogOpen(false);
          setPendingColumnKey(null);
        }}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Sub-campaign folder</DialogTitle>
        <DialogContent>
          <Typography
            className={textClasses.smRegular}
            style={{ color: '#475467', marginBottom: 12 }}
          >
            Choose the folder name for campaigns created from each distinct
            value in this column (up to {MAX_SUB_CAMPAIGN_UNIQUE_VALUES}{' '}
            values).
          </Typography>
          <TextInput
            fullWidth
            label='Folder name'
            value={folderNameInput}
            onChange={(v) => setFolderNameInput(v)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            onClick={() => {
              setFolderDialogOpen(false);
              setPendingColumnKey(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              const key = pendingColumnKey;
              if (!key) {
                setFolderDialogOpen(false);
                return;
              }
              const resolvedFolder = folderNameInput.trim() || 'New Folder';
              if (resolvedFolder.includes('_sub_')) {
                handleError(null, 'Folder name cannot contain "_sub_"');
                return;
              }
              const originSourceColumnName =
                key === SEPARATE_SUB_CAMPAIGNS_COLUMN
                  ? configuredSubCampaignSourceColumn?.trim() || key
                  : key;
              applyColumnMapping(key, SEPARATE_SUB_CAMPAIGNS_COLUMN, {
                skipSubCampaignNotify: true,
              });
              onSubCampaignFolderConfirm?.(
                resolvedFolder,
                originSourceColumnName
              );
              setFolderDialogOpen(false);
              setPendingColumnKey(null);
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
